import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.haap-naturlab.no";
const DEFAULT_OG_IMAGE =
  "https://rvpirrrwpasphlirukef.supabase.co/storage/v1/object/public/brand-images/cbab4041-35cb-43ab-a927-605a6ef2fc6d/logo_onboarding.png";

interface SeoProps {
  title: string;
  description: string;
  /** Path without leading slash, e.g. "om-oss". Empty string = homepage. */
  path?: string;
  /** Absolute image URL. Defaults to the brand logo. */
  image?: string;
  /** "website" for most pages, "product" for product detail. */
  ogType?: "website" | "product" | "article";
  /** Optional extra JSON-LD to append. */
  jsonLd?: unknown;
  /** When true, add noindex,nofollow (checkout callback pages). */
  noIndex?: boolean;
}

/**
 * Per-page SEO wrapper using react-helmet-async. Renders title, description,
 * canonical URL, OpenGraph tags, Twitter Card tags, and (optionally) JSON-LD.
 *
 * `<HelmetProvider>` must wrap the app — set up in src/App.tsx.
 */
export function Seo({
  title,
  description,
  path = "",
  image = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd,
  noIndex = false,
}: SeoProps) {
  const url = `${SITE_URL}/${path.replace(/^\//, "")}`.replace(/\/$/, "") || SITE_URL;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="nb_NO" />
      <meta property="og:site_name" content="Håp Naturlab" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
